import { useEffect, useState } from "react";
import { Button } from "@/components/ui/shadcn/button";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/shadcn/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/shadcn/tooltip";
import { tradesAPI } from "@/services/api";
import { Trade } from "@/types/trades";
import { CheckIcon, ClipboardIcon, ChevronDownIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/error-handler";
import { formatAssetAmount } from "@/lib/utils";
import { truncateAddress } from "@/utils/crypto-address";
import { DateEnhancedTrade } from "@/types/trades";
import {
  SortableTableHeader,
  useTableSorting,
} from "@/components/dashboard/data-table/SortableTableHeader";
import { useColumnVisibility } from "@/components/dashboard/data-table/ColumnVisibility";
import { BaseDataTable } from "@/components/dashboard/data-table/BaseDataTable";
import { QuoteSide, QuoteUnit } from "@/types/quotes";

// Define the sortable keys type
type SortableKey =
  | keyof Omit<Trade, "client" | "crypto_currency" | "quote">
  | "client_name"
  | "asset_pair"
  | "executed_at_date"
  | "quote.price";

// Define column visibility configuration
const initialColumnVisibility = {
  client_name: { label: "Client", visible: true },
  asset_pair: { label: "Asset Pair", visible: true },
  from: { label: "From", visible: true },
  to: { label: "To", visible: true },
  side: { label: "Side", visible: true },
  quoted_price: { label: "Quoted Price", visible: true },
  execution_price: { label: "Execution Price", visible: true },
  fees_bps: { label: "Fees (bps)", visible: true },
  executed_at_date: { label: "Executed At", visible: true },
};

function formatFixedDecimals(value: string | number, decimals: number): string {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return num.toFixed(decimals);
}

export function TradeExecutionsTab({
  handleExportCSV,
}: {
  handleExportCSV: () => void;
}) {
  const { toast } = useToast();
  const [trades, setTrades] = useState<DateEnhancedTrade[]>([]);
  const [filteredTrades, setFilteredTrades] = useState<DateEnhancedTrade[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedVmarketIds, setSelectedVmarketIds] = useState<number[]>([]);
  const [cryptoAddress, setCopiedAddress] = useState<string | null>(null);
  const [copiedURL, setCopiedQuoteUrl] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [editPriceTradeId, setEditPriceTradeId] = useState<number | null>(null);
  const [editPriceValue, setEditPriceValue] = useState<string>("");
  const [editPriceLoading, setEditPriceLoading] = useState(false);

  // Use the new sorting hook
  const { sortConfig, handleSort } = useTableSorting<SortableKey>(
    "executed_at_date",
    "desc"
  );

  // Use the column visibility hook
  const { columns, setColumns, isColumnVisible } = useColumnVisibility(
    initialColumnVisibility,
    "trade-executions-column-visibility"
  );

  // Handle column visibility reset
  const handleResetColumns = () => {
    setColumns(initialColumnVisibility);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedVmarketIds, startDate, endDate]);

  const fetchTrades = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await tradesAPI.getTrades({
        page,
        size,
        vmarket_ids: selectedVmarketIds.length > 0 ? selectedVmarketIds : undefined,
        start_date: startDate ? startDate.toISOString() : undefined,
        end_date: endDate ? endDate.toISOString() : undefined,
      });

      // Enhance trades with Date objects and numeric values for easier sorting
      const enhancedTrades = response.items.map((trade) => ({
        ...trade,
        executed_at_date: DateTime.fromISO(trade.executed_at).toJSDate(),
        total_fiat_numeric: parseFloat(trade.total_to),
        total_crypto_numeric: parseFloat(trade.total_from),
      }));

      setTrades(enhancedTrades);
      setFilteredTrades(enhancedTrades);
      setTotal(response.total);
      setPages(response.pages);

      if (import.meta.env.DEV) {
        console.log("Trades fetched successfully:", {
          count: response.items.length,
          total: response.total,
          page,
          size,
          pages: response.pages,
          selectedVmarketIds,
          startDate,
          endDate,
        });
      }
    } catch (err) {
      handleApiError("Fetch Trades", err, toast);
      setError("Failed to fetch trades. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [page, size, selectedVmarketIds, startDate, endDate]);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard
      .writeText(address)
      .then(() => {
        setCopiedAddress(address);
        toast({
          title: "Success",
          description: "Address copied to clipboard",
          variant: "success",
        });
        setTimeout(() => setCopiedAddress(null), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy address:", error);
        toast({
          title: "Error",
          description: "Failed to copy address to clipboard",
          variant: "error",
        });
      });
  };

  const publicHttpUrl =
    import.meta.env.VITE_PUBLIC_HTTP_URL || "http://localhost:5173";

  const handleCopyToClipboard = (quoteUrl: string) => {
    navigator.clipboard
      .writeText(quoteUrl)
      .then(() => {
        setCopiedQuoteUrl(quoteUrl);
        toast({
          title: "Copied",
          description: "Quote URL copied to clipboard",
          variant: "success",
        });

        // Auto-clear the copied status after 2 seconds
        setTimeout(() => setCopiedQuoteUrl(null), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy link:", error);
        toast({
          title: "Error",
          description: "Failed to copy link to clipboard",
          variant: "error",
        });
      });
  };

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleEditOrSaveExecutionPrice = async (trade: DateEnhancedTrade, maxDecimals: number) => {
    if (editPriceTradeId !== trade.id) {
      setEditPriceTradeId(trade.id);
      setEditPriceValue(formatFixedDecimals(trade.execution_price, maxDecimals));
    } else {
      // Save logic
      if (!editPriceValue || editPriceValue === trade.execution_price) return;
      setEditPriceLoading(true);
      try {
        const updated = await tradesAPI.updateTradeExecutionPrice(trade.id, editPriceValue);
        setTrades(prev => prev.map(t => t.id === trade.id ? { ...t, execution_price: updated.execution_price } : t));
        setFilteredTrades(prev => prev.map(t => t.id === trade.id ? { ...t, execution_price: updated.execution_price } : t));
        setEditPriceTradeId(null);
        toast({
          title: "Success",
          description: "Execution price updated",
          variant: "success",
        });
      } catch (err) {
        handleApiError("Update Execution Price", err, toast);
      } finally {
        setEditPriceLoading(false);
      }
    }
  };

  const renderTableHeader = ({
    sortConfig,
    handleSort,
    isColumnVisible,
  }: {
    sortConfig: { key: string; direction: "asc" | "desc" | null };
    handleSort: (key: string) => void;
    isColumnVisible: (key: string) => boolean;
  }) => (
    <>
      {isColumnVisible("client_name") && (
        <TableHead>
          <SortableTableHeader
            label="Client"
            sortKey="client_name"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("asset_pair") && (
        <TableHead>
          <SortableTableHeader
            label="Asset Pair"
            sortKey="asset_pair"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("side") && (
        <TableHead>
          <SortableTableHeader
            label="Side"
            sortKey="side"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("from") && <TableHead>From</TableHead>}
      {isColumnVisible("to") && <TableHead>To</TableHead>}
      {isColumnVisible("quoted_price") && <TableHead>Quoted Price</TableHead>}
      {isColumnVisible("execution_price") && <TableHead>Execution Price</TableHead>}
      {isColumnVisible("fees_bps") && (
        <TableHead>
          <SortableTableHeader
            label="Fees (bps)"
            sortKey="fees_bps"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("executed_at_date") && (
        <TableHead>
          <SortableTableHeader
            label="Executed At"
            sortKey="executed_at_date"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      <TableHead className="w-0"></TableHead>
    </>
  );

  const renderTableRow = (trade: DateEnhancedTrade) => {
    const executedAt = DateTime.fromISO(trade.executed_at);
    const formattedDate = executedAt.toFormat("LLL dd, yyyy HH:mm:ss");
    const quoteUrl = `${publicHttpUrl}/client/accept-quote/${trade.slug}`;
    const isExpanded = expandedRow === trade.id;

    const isBuy = trade.side === QuoteSide.BUY;

    // For BUY:
    //   - total_from is always in counter (MXN)
    //   - total_to is always in base (BTC)
    // For SELL:
    //   - total_from is always in base (BTC)
    //   - total_to is always in counter (MXN)
    const fromAmount = isBuy
      ? trade.total_from // BUY: Give counter (MXN)
      : trade.total_from; // SELL: Give base (BTC)

    const toAmount = isBuy
      ? trade.total_to // BUY: Get base (BTC)
      : trade.total_to; // SELL: Get counter (MXN)

    // For BUY: fromAsset is counter (MXN), toAsset is base (BTC)
    // For SELL: fromAsset is base (BTC), toAsset is counter (MXN)
    const fromAsset = isBuy ? trade.vmarket.counter : trade.vmarket.base;
    const toAsset = isBuy ? trade.vmarket.base : trade.vmarket.counter;

    const maxDecimals = toAsset.contract_decimals ?? 18;

    return (
      <>
        <TableRow
          key={trade.id}
          className={`hover:bg-gray-100 transition cursor-pointer ${
            isExpanded ? "bg-gray-100" : ""
          }`}
          onClick={() => toggleRow(trade.id)}
        >
          {isColumnVisible("client_name") && (
            <TableCell>
              <span className="cursor-pointer underline decoration-dotted">
                {trade.client.name}
              </span>
            </TableCell>
          )}
          {isColumnVisible("asset_pair") && (
            <TableCell>
              {`${trade.vmarket.base.code}/${trade.vmarket.counter.code}`}
            </TableCell>
          )}
          {isColumnVisible("side") && (
            <TableCell>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  trade.side === QuoteSide.BUY
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {trade.side}
              </span>
            </TableCell>
          )}
          {isColumnVisible("from") && (
            <TableCell>
              {`${formatAssetAmount(fromAmount, fromAsset)} ${fromAsset.code}`}
            </TableCell>
          )}
          {isColumnVisible("to") && (
            <TableCell>
              {`${formatAssetAmount(toAmount, toAsset)} ${toAsset.code}`}
            </TableCell>
          )}
          {isColumnVisible("quoted_price") && (
            <TableCell>
              {`${trade.vmarket.counter.code} ${formatAssetAmount(
                trade.price,
                trade.vmarket.counter
              )}`}
            </TableCell>
          )}
          {isColumnVisible("execution_price") && (
            <TableCell>
              {`${trade.vmarket.counter.code} ${formatAssetAmount(
                trade.execution_price,
                trade.vmarket.counter
              )}`}
            </TableCell>
          )}
          {isColumnVisible("fees_bps") && (
            <TableCell>{trade.fees_bps}</TableCell>
          )}
          {isColumnVisible("executed_at_date") && (
            <TableCell>{formattedDate}</TableCell>
          )}
          <TableCell className="w-0">
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                isExpanded ? "transform rotate-180" : ""
              }`}
            />
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow className="bg-gray-50">
            <TableCell colSpan={9}>
              <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Trade Details</h3>
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-700">
                    Executed
                  </span>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column - Information Cards Side by Side */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Client Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Client Information</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Name:</span>
                          <span className="font-medium text-gray-900">{trade.client.name}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Email:</span>
                          <span className="font-medium text-gray-900">{trade.client.email}</span>
                        </div>
                        {trade.client.phone && (
                          <div className="flex items-center">
                            <span className="text-gray-500 w-14">Phone:</span>
                            <span className="font-medium text-gray-900">{trade.client.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Trade Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Trade Details </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Asset Pair:</span>
                          <span className="font-medium text-gray-900">
                            {trade.vmarket.base.code}/{trade.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Amount:</span>
                          <span className="font-medium text-gray-900">
                            {formatAssetAmount(
                              trade.amount.toString(),
                              trade.unit === QuoteUnit.BASE ? trade.vmarket.base : trade.vmarket.counter
                            )}
                            {" "}
                            {trade.unit === QuoteUnit.BASE ? trade.vmarket.base.code : trade.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Side:
                          </span>
                          <span className="font-medium text-gray-900">
                            {trade.side.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Quoted Price:
                          </span>
                          <span className="font-medium text-gray-900">
                            {`${trade.vmarket.counter.code} ${formatAssetAmount(
                              trade.price,
                              trade.vmarket.counter
                            )}`}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Execution Price:
                          </span>
                          <span className="font-medium text-gray-900">
                            {`${trade.vmarket.counter.code} ${formatAssetAmount(
                              trade.execution_price,
                              trade.vmarket.counter
                            )}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - URL and Crypto Address */}
                  <div className="space-y-4">
                    {/* Quote URL Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Quote URL</h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        <div className="flex-1 font-mono text-[0.9rem] text-gray-600 break-all mr-4 py-2 mt-1">
                          <a href={quoteUrl} target="_blank" rel="noopener noreferrer">{truncateAddress(quoteUrl, 19, 8)}</a>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyToClipboard(quoteUrl);
                              }}
                              className="text-[0.8rem] bg-white hover:bg-gray-50 px-3 max-h-8"
                            >
                              {copiedURL === quoteUrl ? (
                                <span className="flex items-center"><CheckIcon className="max-w-[12px] max-h-[12px] mr-2 text-green-500" /> Copied</span>
                              ) : (
                                <span className="flex items-center"><ClipboardIcon className="max-w-[12px] max-h-[12px] mr-2" /> Copy URL</span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy quote URL to clipboard</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Crypto Address Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Crypto Address</h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        {trade.crypto_address ? (
                          <>
                            <div className="flex-1 font-mono text-[0.9rem] text-gray-600 break-all mr-4 py-2 mt-1">
                              {trade.crypto_address}
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (trade.crypto_address) {
                                      handleCopyAddress(trade.crypto_address);
                                    }
                                  }}
                                  className="text-[0.8rem] bg-white hover:bg-gray-50 px-3 max-h-8"
                                >
                                  {cryptoAddress === trade.crypto_address ? (
                                    <span className="flex items-center"><CheckIcon className="max-w-[12px] max-h-[12px] mr-2 text-green-500" />Copied</span>
                                  ) : (
                                    <span className="flex items-center"><ClipboardIcon className="max-w-[12px] max-h-[12px] mr-2" />Copy Address</span>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Copy crypto address to clipboard</p>
                              </TooltipContent>
                            </Tooltip>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm italic">
                            Not provided
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Update Execution Price Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Execution Price</h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        {editPriceTradeId === trade.id ? (
                          <input
                            type="number"
                            value={editPriceValue}
                            onChange={(e) => {
                              let value = e.target.value;
                              if (value.includes(".")) {
                                const [intPart, decPart] = value.split(".");
                                if (decPart.length > maxDecimals) {
                                  value = `${intPart}.${decPart.slice(0, maxDecimals)}`;
                                }
                              }
                              setEditPriceValue(value);
                            }}
                            className="flex-1 text-[0.9rem] text-gray-600 mr-4 py-2 mt-1 border border-gray-300 rounded px-2"
                            min="0"
                            step="any"
                          />
                        ) : (
                          <div className="flex-1 text-[0.9rem] text-gray-600 mr-4 py-2 mt-1">
                            {`${trade.vmarket.counter.code} ${formatFixedDecimals(trade.execution_price, maxDecimals)}`}
                          </div>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={() => handleEditOrSaveExecutionPrice(trade, maxDecimals)}
                              className="text-[0.8rem] bg-white hover:bg-gray-50 px-3 max-h-8"
                              disabled={editPriceTradeId === trade.id && (editPriceLoading || !editPriceValue || editPriceValue === trade.execution_price)}
                            >
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {editPriceTradeId === trade.id ? (editPriceLoading ? 'Saving...' : 'Save') : 'Update Price'}
                              </span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Update the execution price for this trade</p>
                          </TooltipContent>
                        </Tooltip>
                        {editPriceTradeId === trade.id && (
                          <Button
                            variant="ghost"
                            onClick={e => {
                              e.stopPropagation();
                              setEditPriceTradeId(null);
                            }}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                      {editPriceTradeId === trade.id && (
                        <p className="text-xs text-gray-500 mt-1">Up to {maxDecimals} decimal places allowed</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <BaseDataTable
        title="Trade Executions"
        description="List of executed trades"
        data={trades}
        filteredData={filteredTrades}
        setFilteredData={setFilteredTrades}
        loading={loading}
        error={error}
        onRetry={fetchTrades}
        onExportCSV={handleExportCSV}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedVmarketIds={selectedVmarketIds}
        setSelectedVmarketIds={setSelectedVmarketIds}
        columns={columns}
        default_columns={initialColumnVisibility}
        storageKey="trade-executions-column-visibility"
        setColumns={setColumns}
        sortableKeys={["client_name", "asset_pair", "executed_at_date"]}
        defaultSortKey="executed_at_date"
        defaultSortDirection="desc"
        renderTableHeader={renderTableHeader}
        renderTableRow={renderTableRow}
        withQuoteTypeBoxes={true}
        pagination={{
          page,
          setPage,
          size,
          setSize,
          total,
          pages,
        }}
        onResetColumns={handleResetColumns}
      />
    </div>
  );
}
